"use client";

import React, { useRef, useState } from "react";
import Image from "next/image";
import { Link } from "@/i18n/navigation";
import { useLocale } from "next-intl";

// Icons
import {
  MdArrowForward,
  MdStar,
  MdFavoriteBorder,
  MdOutlineHome,
  MdOutlineHandshake,
  MdOutlineTaskAlt,
  MdLocationOn,
  MdOutlineChevronRight,
  MdOutlineChevronLeft,
  MdOutlinePeople,
} from "react-icons/md";

import styles from "./trusted-agents.module.css";

const AGENTS_DATA = [
  {
    id: 1,
    name: "Andrei Popescu",
    role: "Luxury Property Specialist",
    location: "Bucharest, Romania",
    rating: 4.9,
    properties: 125,
    experience: "8+",
    closedDeals: "150+",
    image: "/placeholder-images/placeholder1.jpg",
  },
  {
    id: 2,
    name: "Elena Ionescu",
    role: "Residential Expert",
    location: "Cluj-Napoca, Romania",
    rating: 4.8,
    properties: 98,
    experience: "6+",
    closedDeals: "110+",
    image: "/placeholder-images/placeholder2.jpg",
  },
  {
    id: 3,
    name: "Vlad Matei",
    role: "Investment Adviser",
    location: "Bucharest, Romania",
    rating: 4.9,
    properties: 142,
    experience: "10+",
    closedDeals: "200+",
    image: "/placeholder-images/placeholder3.jpg",
  },
  {
    id: 4,
    name: "Maria Dumitru",
    role: "Commercial Real Estate",
    location: "Timisoara, Romania",
    rating: 4.7,
    properties: 65,
    experience: "5+",
    closedDeals: "85+",
    image: "/placeholder-images/placeholder1.jpg",
  },
];

const AGENCIES_DATA = [
  {
    id: "ag1",
    name: "Prestige Estates",
    location: "Dubai, UAE",
    rating: 4.9,
    activeProperties: 450,
    agentCount: 32,
    image: "/logo.png",
  },
  {
    id: "ag2",
    name: "Global Luxury Realty",
    location: "Miami, United States",
    rating: 4.8,
    activeProperties: 320,
    agentCount: 24,
    image: "/logo_black.png",
  },
  {
    id: "ag3",
    name: "Luxury Properties",
    location: "London, United Kingdom",
    rating: 4.9,
    activeProperties: 512,
    agentCount: 45,
    image: "/small_logo_black.png",
  },
  {
    id: "ag4",
    name: "Coastal Realty",
    location: "Los Angeles, United States",
    rating: 4.7,
    activeProperties: 210,
    agentCount: 18,
    image: "/placeholder-images/coastal-logo.png",
  },
];

export default function TrustedAgents() {
  const locale = useLocale();
  const isRTL = locale === "ar" || locale === "he";
  const scrollAmount = isRTL ? -320 : 320;

  // --- Agents Drag State ---
  const agentsRef = useRef<HTMLDivElement | null>(null);
  const [isDraggingAgents, setIsDraggingAgents] = useState(false);
  const [startXAgents, setStartXAgents] = useState(0);
  const [scrollLeftAgents, setScrollLeftAgents] = useState(0);
  const [hasDraggedAgents, setHasDraggedAgents] = useState(false);

  // --- Agencies Drag State ---
  const agenciesRef = useRef<HTMLDivElement | null>(null);
  const [isDraggingAgencies, setIsDraggingAgencies] = useState(false);
  const [startXAgencies, setStartXAgencies] = useState(0);
  const [scrollLeftAgencies, setScrollLeftAgencies] = useState(0);
  const [hasDraggedAgencies, setHasDraggedAgencies] = useState(false);

  // --- Scroll Actions ---
  const handleScroll = (
    ref: React.RefObject<HTMLDivElement | null>,
    direction: "left" | "right",
  ) => {
    if (ref.current) {
      const amount = direction === "left" ? -scrollAmount : scrollAmount;
      ref.current.scrollBy({ left: amount, behavior: "smooth" });
    }
  };

  return (
    <section className={styles.section}>
      {/* --- AGENTS HEADER --- */}
      <div className={styles.header}>
        <div className={styles.titleWrapper}>
          <h2 className={styles.title}>
            Find Your Trusted Real Estate Partner
          </h2>
          <p className={styles.subtitle}>
            Work with verified professionals who put your interests first.
          </p>
        </div>
        <Link href="/agents" className={styles.viewAllLink}>
          View all agents
          <MdArrowForward className={styles.linkIcon} />
        </Link>
      </div>

      {/* --- AGENTS TRACK --- */}
      <div className={styles.sliderWrapper}>
        <div
          className={`${styles.cardsTrack} ${isDraggingAgents ? styles.dragging : ""}`}
          ref={agentsRef}
          dir={isRTL ? "rtl" : "ltr"}
          onMouseDown={(e) => {
            if (!agentsRef.current) return;
            setIsDraggingAgents(true);
            setHasDraggedAgents(false);
            setStartXAgents(e.pageX - agentsRef.current.offsetLeft);
            setScrollLeftAgents(agentsRef.current.scrollLeft);
          }}
          onMouseLeave={() => setIsDraggingAgents(false)}
          onMouseUp={() => {
            setIsDraggingAgents(false);
            setTimeout(() => setHasDraggedAgents(false), 50);
          }}
          onMouseMove={(e) => {
            if (!isDraggingAgents || !agentsRef.current) return;
            e.preventDefault();
            const x = e.pageX - agentsRef.current.offsetLeft;
            const walk = (x - startXAgents) * 2;
            agentsRef.current.scrollLeft = scrollLeftAgents - walk;
            if (Math.abs(walk) > 5) setHasDraggedAgents(true);
          }}
        >
          {AGENTS_DATA.map((agent) => (
            <div
              key={agent.id}
              className={styles.agentWrapper}
              onClickCapture={(e) => {
                if (hasDraggedAgents) {
                  e.stopPropagation();
                  e.preventDefault();
                }
              }}
            >
              <Link
                href={`/agents/${agent.id}`}
                className={styles.engineeredCard}
                draggable={false}
              >
                {/* 1. Header Area with Action */}
                <div className={styles.cardHeaderArea}>
                  <button
                    className={styles.favoriteBtn}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    aria-label="Save Agent"
                  >
                    <MdFavoriteBorder />
                  </button>

                  <div className={styles.avatarWrapper}>
                    <Image
                      src={agent.image}
                      alt={agent.name}
                      fill
                      sizes="96px"
                      className={styles.agentAvatar}
                      draggable={false}
                    />
                  </div>
                </div>

                {/* 2. Main Profile Content */}
                <div className={styles.cardContent}>
                  <div className={styles.titleRow}>
                    <h3 className={styles.agentName}>{agent.name}</h3>
                    <div className={styles.ratingBadge}>
                      <MdStar className={styles.starIconSolid} />
                      <span className={styles.ratingScore}>{agent.rating}</span>
                    </div>
                  </div>

                  <p className={styles.agentRole}>{agent.role}</p>

                  <div className={styles.locationWrapper}>
                    <MdLocationOn className={styles.locationIcon} />
                    <span className={styles.agentLocation}>
                      {agent.location}
                    </span>
                  </div>
                </div>

                {/* 3. Translation-Safe Vertical Stats List */}
                <div className={styles.statsList}>
                  <div className={styles.statLine}>
                    <MdOutlineHome className={styles.statLineIcon} />
                    <span className={styles.statLineValue}>
                      {agent.properties}
                    </span>
                    <span className={styles.statLineText}>
                      Active Properties
                    </span>
                  </div>
                  <div className={styles.statLine}>
                    <MdOutlineHandshake className={styles.statLineIcon} />
                    <span className={styles.statLineValue}>
                      {agent.experience}
                    </span>
                    <span className={styles.statLineText}>
                      Years Experience
                    </span>
                  </div>
                  <div className={styles.statLine}>
                    <MdOutlineTaskAlt className={styles.statLineIcon} />
                    <span className={styles.statLineValue}>
                      {agent.closedDeals}
                    </span>
                    <span className={styles.statLineText}>Closed Deals</span>
                  </div>
                </div>
              </Link>
            </div>
          ))}
        </div>

        {/* Agents Controls */}
        <div className={styles.carouselControls}>
          <button
            className={styles.controlBtn}
            aria-label="Previous"
            onClick={() => handleScroll(agentsRef, "left")}
          >
            <MdOutlineChevronLeft className={styles.controlIcon} />
          </button>
          <button
            className={styles.controlBtn}
            aria-label="Next"
            onClick={() => handleScroll(agentsRef, "right")}
          >
            <MdOutlineChevronRight className={styles.controlIcon} />
          </button>
        </div>
      </div>

      <div className={styles.sectionDivider} />

      {/* --- AGENCIES HEADER --- */}
      <div className={`${styles.header} ${styles.secondaryHeader}`}>
        <div className={styles.titleWrapper}>
          <h3 className={styles.title}>Top Real Estate Agencies</h3>
          <p className={styles.subtitle}>
            Partner with the most trusted agencies worldwide.
          </p>
        </div>
        <Link href="/agencies" className={styles.viewAllLink}>
          View all agencies
          <MdArrowForward className={styles.linkIcon} />
        </Link>
      </div>

      {/* --- AGENCIES TRACK --- */}
      <div className={styles.sliderWrapper}>
        <div
          className={`${styles.cardsTrack} ${isDraggingAgencies ? styles.dragging : ""}`}
          ref={agenciesRef}
          dir={isRTL ? "rtl" : "ltr"}
          onMouseDown={(e) => {
            if (!agenciesRef.current) return;
            setIsDraggingAgencies(true);
            setHasDraggedAgencies(false);
            setStartXAgencies(e.pageX - agenciesRef.current.offsetLeft);
            setScrollLeftAgencies(agenciesRef.current.scrollLeft);
          }}
          onMouseLeave={() => setIsDraggingAgencies(false)}
          onMouseUp={() => {
            setIsDraggingAgencies(false);
            setTimeout(() => setHasDraggedAgencies(false), 50);
          }}
          onMouseMove={(e) => {
            if (!isDraggingAgencies || !agenciesRef.current) return;
            e.preventDefault();
            const x = e.pageX - agenciesRef.current.offsetLeft;
            const walk = (x - startXAgencies) * 2;
            agenciesRef.current.scrollLeft = scrollLeftAgencies - walk;
            if (Math.abs(walk) > 5) setHasDraggedAgencies(true);
          }}
        >
          {AGENCIES_DATA.map((agency) => (
            <div
              key={agency.id}
              className={styles.agencyWrapper}
              onClickCapture={(e) => {
                if (hasDraggedAgencies) {
                  e.stopPropagation();
                  e.preventDefault();
                }
              }}
            >
              <Link
                href={`/agencies/${agency.id}`}
                className={styles.engineeredCard}
                draggable={false}
              >
                <div className={styles.agencyLogoContainer}>
                  <Image
                    src={agency.image}
                    alt={agency.name}
                    fill
                    sizes="(max-width: 768px) 50vw, 240px"
                    className={styles.agencyLogo}
                    draggable={false}
                  />
                </div>
                <div className={styles.agencyContentBox}>
                  <div className={styles.agencyTitleRow}>
                    <h4 className={styles.agencyName}>{agency.name}</h4>
                    <div className={styles.ratingBadge}>
                      <MdStar className={styles.starIconSolid} />
                      <span className={styles.ratingScore}>
                        {agency.rating}
                      </span>
                    </div>
                  </div>
                  <p className={styles.agencyLocation}>{agency.location}</p>

                  {/* Agency KPIs instead of reviews */}
                  <div className={styles.agencyKpiList}>
                    <div className={styles.kpiItem}>
                      <MdOutlineHome className={styles.kpiIcon} />
                      <span>
                        <strong>{agency.activeProperties}</strong> Properties
                      </span>
                    </div>
                    <div className={styles.kpiItem}>
                      <MdOutlinePeople className={styles.kpiIcon} />
                      <span>
                        <strong>{agency.agentCount}</strong> Agents
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          ))}
        </div>

        {/* Agencies Controls */}
        <div className={styles.carouselControls}>
          <button
            className={styles.controlBtn}
            aria-label="Previous"
            onClick={() => handleScroll(agenciesRef, "left")}
          >
            <MdOutlineChevronLeft className={styles.controlIcon} />
          </button>
          <button
            className={styles.controlBtn}
            aria-label="Next"
            onClick={() => handleScroll(agenciesRef, "right")}
          >
            <MdOutlineChevronRight className={styles.controlIcon} />
          </button>
        </div>
      </div>
    </section>
  );
}
